import { useCallback, useEffect, useState } from 'react';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import apiCoreAdapter from './apiCoreAdapter';
import { fetchRangeEarlyPaymentsBe, fetchRangePeriodPaymentsBe, RangePeriodPaymentBe } from './api/liquidaciones';
import type { Session } from '../types/session';
import { BA_TIMEZONE } from '../utils/timezone';
import {
  calculateNZSettlementSchedule,
  MonthlySettledSessions,
  NZSettlementMonthResult,
  NZ_SETTLEMENT_SCHEME_START_MONTH,
  NZ_ACCUMULATION_SESSION_STATUSES,
  EarlyPaymentInfo,
  EarlyRequestInfo,
} from '../utils/nzLiquidacion';

function monthKeyFromPeriod(periodYear: number, periodMonth: number): string {
  return `${periodYear}-${String(periodMonth).padStart(2, '0')}`;
}

interface EarlyData {
  /** 'YYYY-MM' -> sesiones pagadas como anticipada ese mes (lo que descuenta el cálculo). */
  sessionsPaidByMonth: Record<string, number>;
  /** 'YYYY-MM' -> el pago completo (para mostrarlo: monto y fecha). */
  paymentByMonth: Record<string, EarlyPaymentInfo>;
  /** 'YYYY-MM' -> la ÚLTIMA solicitud de anticipada de ese mes. */
  latestRequestByMonth: Record<string, EarlyRequestInfo>;
}

const EMPTY_EARLY_DATA: EarlyData = { sessionsPaidByMonth: {}, paymentByMonth: {}, latestRequestByMonth: {} };

/** Pagos anticipados ya informados por finanzas-gitlab y solicitudes de la propia psicóloga,
 *  agrupados por mes. Best-effort: si el fetch falla (endpoint caído, sin datos todavía), se
 *  sigue mostrando el schedule igual que antes de que existiera esta integración — no
 *  bloquea "Tus liquidaciones" por esto, es un ajuste sobre el cálculo, no su base. */
async function fetchEarlyData(): Promise<EarlyData> {
  try {
    const resp = await fetchRangeEarlyPaymentsBe();
    const data: EarlyData = { sessionsPaidByMonth: {}, paymentByMonth: {}, latestRequestByMonth: {} };
    for (const pago of resp.pagos) {
      const key = monthKeyFromPeriod(pago.periodYear, pago.periodMonth);
      data.sessionsPaidByMonth[key] = pago.sessionsPaid;
      data.paymentByMonth[key] = { sessionsPaid: pago.sessionsPaid, amountPaid: pago.amountPaid, paidAt: pago.paidAt };
    }
    // No se asume el orden del backend: la última solicitud de cada mes es la de `requestedAt` mayor.
    for (const solicitud of resp.solicitudes ?? []) {
      const key = monthKeyFromPeriod(solicitud.periodYear, solicitud.periodMonth);
      const actual = data.latestRequestByMonth[key];
      if (!actual || new Date(solicitud.requestedAt).getTime() > new Date(actual.requestedAt).getTime()) {
        data.latestRequestByMonth[key] = {
          requestId: solicitud.requestId,
          requestedAt: solicitud.requestedAt,
          requestedSessions: solicitud.requestedSessions,
          estimatedAmount: solicitud.estimatedAmount,
          dismissedAt: solicitud.dismissedAt ?? null,
          dismissalReason: solicitud.dismissalReason ?? null,
        };
      }
    }
    return data;
  } catch {
    return EMPTY_EARLY_DATA;
  }
}

/** Pagos de tramo ya congelados (verificados contra el recálculo independiente de
 *  psiplatform-be), agrupados por mes ('YYYY-MM' -> el registro completo). Best-effort,
 *  mismo criterio que fetchEarlyData: si falla, el schedule sigue mostrando el
 *  cálculo en vivo para todos los meses en vez de romper "Tus liquidaciones" por esto. */
async function fetchFrozenPeriodsByMonth(): Promise<Record<string, RangePeriodPaymentBe>> {
  try {
    const resp = await fetchRangePeriodPaymentsBe();
    const byMonth: Record<string, RangePeriodPaymentBe> = {};
    for (const periodo of resp.periodos) {
      byMonth[monthKeyFromPeriod(periodo.periodYear, periodo.periodMonth)] = periodo;
    }
    return byMonth;
  } catch {
    return {};
  }
}

export interface UseNZSettlementScheduleResult {
  schedule: NZSettlementMonthResult[];
  /** Las mismas sesiones settled ya traídas para calcular schedule, agrupadas por mes
   *  calendario ('YYYY-MM') y ordenadas por fecha ascendente — para mostrar el detalle de
   *  qué sesiones componen cada mes/acumulado sin pegarle de nuevo a la API. */
  sessionsByMonth: Record<string, Session[]>;
  /** Meses ('YYYY-MM') con un pago de tramo ya congelado — ver
   *  RangePeriodPayment/congelarRangePayment. Cuando existe, ese mes ya no es una
   *  proyección: la UI debe mostrar este registro tal cual en vez de `schedule` (ver
   *  NZLiquidacionSchedule). No participa de `loading`/`error`: best-effort, igual
   *  criterio que earlyPaymentByMonth. */
  frozenPeriodsByMonth: Record<string, RangePeriodPaymentBe>;
  /** Pago anticipado ya abonado por mes ('YYYY-MM') — monto y fecha para mostrar. Sus sesiones
   *  ya están descontadas en `schedule` (franja sobre el acumulado neto). Best-effort. */
  earlyPaymentByMonth: Record<string, EarlyPaymentInfo>;
  /** Última solicitud de anticipada de la psicóloga por mes ('YYYY-MM'). Best-effort. */
  latestRequestByMonth: Record<string, EarlyRequestInfo>;
  /** Vuelve a leer solo las solicitudes (sin spinner ni recalcular sesiones) — para reflejar de
   *  inmediato una solicitud recién enviada. */
  refreshRequests: () => Promise<void>;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

const SESSIONS_FETCH_TAKE = 5000; // mismo orden de magnitud que MetricsPsiPlusTab para rangos multi-mes

function monthKeyFromSessionDate(sessionDate: string): string {
  // session.date llega en UTC (ISO — GET /psychologist/sessions sin `utc` serializa el
  // Date crudo, ver sessions.service.ts): el mes de liquidación corta en hora argentina,
  // la misma zona de referencia que paymentDueDate.ts (feriados/vencimiento), nunca en UTC
  // ni en la del navegador de la psicóloga — corregido tras el bug real reportado por una
  // psicóloga en Colombia (UTC-5) cuya sesión 20:30 del último día del mes caía después de
  // medianoche UTC y se contaba en el mes siguiente.
  return formatInTimeZone(new Date(sessionDate), BA_TIMEZONE, 'yyyy-MM');
}

/** Todas las claves 'YYYY-MM' entre startMonth y endMonth, ambos inclusive. */
function enumerateMonths(startMonth: string, endMonth: string): string[] {
  const [startYear, startMonthNum] = startMonth.split('-').map(Number);
  const [endYear, endMonthNum] = endMonth.split('-').map(Number);
  const months: string[] = [];
  let year = startYear;
  let month = startMonthNum;
  while (year < endYear || (year === endYear && month <= endMonthNum)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

/**
 * Conecta calculateNZSettlementSchedule (cálculo puro, sin persistencia) a sesiones reales:
 * trae todas las sesiones `settled`/`lost` (ver NZ_ACCUMULATION_SESSION_STATUSES — mismo
 * criterio de acumulación que usa finanzas-gitlab, confirmado con ese proyecto) desde
 * NZ_SETTLEMENT_SCHEME_START_MONTH hasta hoy con apiCoreAdapter.psychologistSessionsList (que sí
 * soporta from/to reales, a diferencia de la llamada directa que usa MetricsLiquidacionTab para
 * el detalle expandible), las agrupa por mes calendario en el cliente (no hay ningún
 * endpoint/hook que ya las devuelva agrupadas así — ver docs/liquidacion-nz-research.md), trae en
 * paralelo los pagos anticipados ya informados por finanzas-gitlab
 * (GET /psychologist/liquidaciones/anticipada) y los pagos de tramo ya congelados
 * (GET /psychologist/liquidaciones/range — ver frozenPeriodsByMonth) y recalcula el
 * schedule completo en cada carga.
 *
 * NO decide si la psicóloga es NZ: ese gate lo resuelve ProfileMetricsTab (journeyMode) y baja
 * por props hasta MetricsLiquidacionTab, que monta este hook solo cuando corresponde. Mirar
 * `rankingCategory` del contexto acá sería un segundo criterio en paralelo al central — el bug
 * que arregló el commit 4e380d1 — y además decidiría con `undefined` si el perfil todavía no
 * cargó (ver el comentario de useNzJourneyStatus sobre quién popula el contexto).
 */
export function useNZSettlementSchedule(): UseNZSettlementScheduleResult {
  const [schedule, setSchedule] = useState<NZSettlementMonthResult[]>([]);
  const [sessionsByMonth, setSessionsByMonth] = useState<Record<string, Session[]>>({});
  const [frozenPeriodsByMonth, setFrozenPeriodsByMonth] = useState<Record<string, RangePeriodPaymentBe>>({});
  const [earlyPaymentByMonth, setEarlyPaymentByMonth] = useState<Record<string, EarlyPaymentInfo>>({});
  const [latestRequestByMonth, setLatestRequestByMonth] = useState<Record<string, EarlyRequestInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  const refreshRequests = useCallback(async () => {
    const early = await fetchEarlyData();
    setLatestRequestByMonth(early.latestRequestByMonth);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(false);
      try {
        const today = new Date();
        // Mismo corte que monthKeyFromSessionDate: "mes actual" es el mes calendario en hora
        // argentina, no en la del navegador (evita el bug hermano de 5a: una psicóloga en una
        // TZ distinta a la argentina podía tener `currentMonth` desalineado del bucket real de
        // su última sesión, y esa sesión quedaba fuera de `enumerateMonths` sin sumar a ningún mes).
        const currentMonth = formatInTimeZone(today, BA_TIMEZONE, 'yyyy-MM');

        const [resp, earlyData, frozenPeriods] = await Promise.all([
          apiCoreAdapter.psychologistSessionsList({
            // Instantes UTC absolutos y no ambiguos (ISO con 'Z'): el backend hace
            // `new Date(query.from/to)`, que los parsea igual sin importar en qué TZ corra el
            // proceso — a diferencia del wall-clock naive anterior, que el server podía
            // reinterpretar en su propia zona y dejar afuera sesiones recientes (bug 5b).
            from: fromZonedTime(`${NZ_SETTLEMENT_SCHEME_START_MONTH}-01T00:00:00`, BA_TIMEZONE).toISOString(),
            to: today.toISOString(),
            take: SESSIONS_FETCH_TAKE,
            status: NZ_ACCUMULATION_SESSION_STATUSES,
          }),
          fetchEarlyData(),
          fetchFrozenPeriodsByMonth(),
        ]);

        if (cancelled) return;

        const sessionsGroupedByMonth = new Map<string, Session[]>();
        for (const session of resp.data) {
          const monthKey = monthKeyFromSessionDate(session.date);
          const bucket = sessionsGroupedByMonth.get(monthKey);
          if (bucket) bucket.push(session);
          else sessionsGroupedByMonth.set(monthKey, [session]);
        }
        sessionsGroupedByMonth.forEach((bucket) => {
          bucket.sort((a, b) => a.date.localeCompare(b.date));
        });

        const months = enumerateMonths(NZ_SETTLEMENT_SCHEME_START_MONTH, currentMonth);
        const monthlySessions: MonthlySettledSessions[] = months.map((month) => ({
          month,
          sessionsCount: sessionsGroupedByMonth.get(month)?.length ?? 0,
        }));

        setSchedule(calculateNZSettlementSchedule(monthlySessions, today, earlyData.sessionsPaidByMonth));
        setSessionsByMonth(Object.fromEntries(sessionsGroupedByMonth));
        setFrozenPeriodsByMonth(frozenPeriods);
        setEarlyPaymentByMonth(earlyData.paymentByMonth);
        setLatestRequestByMonth(earlyData.latestRequestByMonth);
      } catch {
        if (!cancelled) setSchedule([]);
        if (!cancelled) setSessionsByMonth({});
        if (!cancelled) setFrozenPeriodsByMonth({});
        if (!cancelled) setEarlyPaymentByMonth({});
        if (!cancelled) setLatestRequestByMonth({});
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return {
    schedule,
    sessionsByMonth,
    frozenPeriodsByMonth,
    earlyPaymentByMonth,
    latestRequestByMonth,
    refreshRequests,
    loading,
    error,
    refetch,
  };
}
